import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  KanalMotywuKonta,
  MOTYW_DOMYSLNY,
  MOTYW_JASNY,
  MOTYW_CIEMNY,
  czyMotyw,
  czytajWyborMotywu,
  zapiszWyborMotywu,
  sprzatnijZapisMotywuSprzed2026_10,
  ustawKolorPaskaPrzegladarki,
} from '../utils/motywAplikacji';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

/**
 * Motyw aplikacji (html[data-theme]).
 *
 * [polecenie właściciela 2026-10-03] DOMYŚLNIE JASNY — „user to potem sobie zmieni”.
 * BYŁO ŹLE: efekt zapisywał `app-theme` przy KAŻDYM montażu, więc wartość domyślna trafiała do przeglądarki
 * jak wybór człowieka (a do 1.03.2026 domyślny był ciemny). TERAZ efekt tylko ustawia atrybut; zapis powstaje
 * wyłącznie w `ustawMotyw` / `toggleTheme`, czyli z kliknięcia. Szczegóły i klucze: `utils/motywAplikacji.js`.
 *
 * Źródło wyboru:
 *  - w aplikacji — kanał `KanalMotywuKonta` od PersonalizationProvider (wybór zapisany NA KONCIE, pamięć
 *    przeglądarki tylko jako start bez mignięcia);
 *  - bez tego kanału (test, strona awarii) — sama pamięć przeglądarki.
 * API dla komponentów bez zmian: `{ theme, toggleTheme }`; dochodzą `ustawMotyw(t)` i `motywWybrany`
 * (`null` = nikt nie wybierał, obowiązuje domyślny jasny).
 */
export const ThemeProvider = ({ children }) => {
  const kanal = useContext(KanalMotywuKonta);
  const [wyborLokalny, setWyborLokalny] = useState(czytajWyborMotywu);

  const wybor = kanal ? kanal.motyw : wyborLokalny;
  const theme = czyMotyw(wybor) ? wybor : MOTYW_DOMYSLNY;

  // Stary klucz był zapisywany automatycznie — nic nie znaczy, a zostawiony myliłby przy diagnozie.
  useEffect(() => { sprzatnijZapisMotywuSprzed2026_10(); }, []);

  // Tylko atrybut (i pasek przeglądarki). Żadnego zapisu tutaj: wartość domyślna NIE jest wyborem.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    ustawKolorPaskaPrzegladarki(theme);
  }, [theme]);

  const ustawKanal = kanal ? kanal.ustawMotyw : null;
  const ustawMotyw = useCallback((nowy) => {
    if (!czyMotyw(nowy)) return;
    if (typeof ustawKanal === 'function') {
      ustawKanal(nowy);
      return;
    }
    zapiszWyborMotywu(nowy);
    setWyborLokalny(nowy);
  }, [ustawKanal]);

  const toggleTheme = useCallback(() => {
    ustawMotyw(theme === MOTYW_JASNY ? MOTYW_CIEMNY : MOTYW_JASNY);
  }, [theme, ustawMotyw]);

  const value = useMemo(() => ({
    theme,
    toggleTheme,
    ustawMotyw,
    motywWybrany: czyMotyw(wybor) ? wybor : null,
  }), [theme, toggleTheme, ustawMotyw, wybor]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
